import { useMutation } from '@tanstack/react-query';
import { postDownload } from '@/lib/api';
import { ApiError } from '@/lib/types';
import { useDownloadContext } from '@/contexts/DownloadContext';
import { useCallback } from 'react';
import { useNotifications } from './useNotifications';
import { AxiosError } from 'axios';
import {
  DownloadParams,
  DownloadSignedUrlFile,
  buildDownloadTitle,
  saveDownloadedFiles,
} from '@/lib/download';

export type { DownloadParams };

type DownloadMutationVariables = DownloadParams & { id: string };
type RetryMutationVariables = {
  id: string;
  files: DownloadSignedUrlFile[];
  params: DownloadParams;
};

export const useDownload = () => {
  const {
    loading,
    list,
    managerOpen,
    setManagerOpen,
    addDownload,
    updateDownloadById,
    removeDownload,
  } = useDownloadContext();
  const { showError, showSuccess } = useNotifications();

  const reportProgress = useCallback(
    (id: string, completed: number, total: number) => {
      updateDownloadById(id, {
        preparing: false,
        progress: total > 0 ? (completed / total) * 100 : 100,
      });
    },
    [updateDownloadById],
  );

  const { mutate: mutateDownload } = useMutation<
    DownloadSignedUrlFile[],
    AxiosError<ApiError>,
    DownloadMutationVariables
  >({
    mutationFn: ({ id, ...params }) =>
      postDownload(
        params,
        (completed, total) => reportProgress(id, completed, total),
        () =>
          updateDownloadById(id, {
            preparing: true,
            progress: undefined,
          }),
      ),
    onSuccess: (failedFiles, { id }) => {
      if (failedFiles.length === 0) {
        showSuccess('Download started');
        updateDownloadById(id, {
          status: 'completed',
          progress: 100,
        });
      } else {
        updateDownloadById(id, {
          status: 'failed',
          files: failedFiles,
        });
      }
    },
    onError: (error, { id }) => {
      if (error.response?.status === 403) {
        showError('User lacks required permission');
        removeDownload(id);
        return;
      }
      // The request itself failed before we got any files back -
      // there's nothing to list, so a retry has to redo the whole
      // request.
      updateDownloadById(id, {
        status: 'failed',
        preparing: false,
        files: null,
      });
    },
  });

  const { mutate: mutateRetry } = useMutation<
    DownloadSignedUrlFile[],
    unknown,
    RetryMutationVariables
  >({
    mutationFn: ({ id, files, params }) =>
      saveDownloadedFiles(files, params, (completed, total) =>
        reportProgress(id, completed, total),
      ),
    onSuccess: (stillFailedFiles, { id }) => {
      if (stillFailedFiles.length === 0) {
        showSuccess('Download started');
        updateDownloadById(id, {
          status: 'completed',
          progress: 100,
        });
      } else {
        updateDownloadById(id, {
          status: 'failed',
          files: stillFailedFiles,
        });
      }
    },
    onError: (_error, { id }) => {
      showError('Failed to download file');
      updateDownloadById(id, { status: 'failed' });
    },
  });

  const download = useCallback(
    (params: DownloadParams) => {
      if (loading) {
        return;
      }
      const id = addDownload(buildDownloadTitle(params), params);
      mutateDownload({ id, ...params });
    },
    [loading, addDownload, mutateDownload],
  );

  const retryDownload = useCallback(
    (id: string) => {
      if (loading) {
        return;
      }
      const entry = list.find((download) => download.id === id);
      if (!entry) {
        return;
      }

      updateDownloadById(id, {
        status: 'pending',
        preparing: false,
        progress: 0,
      });

      if (entry.files == null) {
        // Whole request failed before any signed urls came back.
        mutateDownload({ id, ...entry.params });
      } else {
        mutateRetry({ id, files: entry.files, params: entry.params });
      }
    },
    [loading, list, updateDownloadById, mutateDownload, mutateRetry],
  );

  const dismissDownload = useCallback(
    (id: string) => {
      removeDownload(id);
    },
    [removeDownload],
  );

  return {
    download,
    loading,
    list,
    managerOpen,
    setManagerOpen,
    retryDownload,
    dismissDownload,
  };
};
